import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Space, Spin } from "antd";
import { Switch } from "shared/components/Components";
import { PlusOutlined } from "@ant-design/icons";
import PermissionWrapper from "shared/components/PermissionWrapper";
import { successfulNotification } from "common/utils/notifications";
import { handleApiError } from "common/utils/handleApiError";
import { applyFormSubmitError } from "common/utils/validationErrors";
import { liveRules, RuleRecord, RuleVocabulary } from "../rules";
import type { RulesApi } from "../rulesApi";
import RuleFormFields, {
  emptyRuleFormValues,
  RULE_FORM_FIELDS,
  RuleFormFieldsProps,
  RuleFormValues,
  ruleToFormValues,
  toRulePayload,
} from "../rule-form/RuleFormFields";
import RulesTable from "../rules-table/RulesTable";
import "./rules-manager.scss";

export interface RulesPermissions {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface RulesManagerProps<TRule extends RuleRecord = RuleRecord>
  extends Pick<RuleFormFieldsProps, "targetHelp" | "targetExtra" | "fieldPathHint"> {
  /** The rules of the definition being managed. */
  api: RulesApi<TRule>;
  vocabulary: RuleVocabulary | null;
  permissions: RulesPermissions;
  /** Whether the owning definition takes part in evaluation (drives the "never matches" warning). */
  definitionActive?: boolean;
  /**
   * The explanation above the table. The default says how many active rules
   * must match (AND) and warns when there are none; a feature overrides it
   * to add its own guidance.
   */
  renderIntro?: (state: { activeRules: TRule[]; viewDeleted: boolean }) => React.ReactNode;
  /** Shown under the default intro's headline. */
  introDescription?: React.ReactNode;
  /** Called after any successful write, so the owner can refresh counts. */
  onRulesChanged?: () => void;
  /** Keys the table's remembered layout (column widths, page size); one per feature, e.g. "project-type-rules". */
  tableId?: string;
  /** The heading in the table's bar. */
  title?: string;
  /** The app context for the table (`useMercuryContext()` when omitted); stories pass a stub. */
  context?: any;
  className?: string;
}

/**
 * The "Manage Rules" tab of a definition: its rules in a table, add / edit in
 * a modal with {@link RuleFormFields}, soft delete with confirmation, and a
 * view-deleted switch. Every rules-driven feature renders this with its own
 * {@link RulesApi} and vocabulary.
 */
function RulesManager<TRule extends RuleRecord = RuleRecord>({
  api,
  vocabulary,
  permissions: { canCreate, canEdit, canDelete },
  definitionActive = true,
  renderIntro,
  introDescription,
  onRulesChanged,
  targetHelp,
  targetExtra,
  fieldPathHint,
  tableId = "rules",
  title = "Rules",
  context,
  className,
}: RulesManagerProps<TRule>) {
  const [rules, setRules] = useState<TRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewDeleted, setViewDeleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<TRule | null>(null);
  const [deleting, setDeleting] = useState<TRule | null>(null);
  const [addForm] = Form.useForm<RuleFormValues>();
  const [editForm] = Form.useForm<RuleFormValues>();

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      setRules(await api.list(viewDeleted));
    } catch (error) {
      handleApiError(error, "Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, [api, viewDeleted]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const afterWrite = () => {
    fetchRules();
    onRulesChanged?.();
  };

  const submit = async (
    values: RuleFormValues,
    form: typeof addForm,
    write: (payload: ReturnType<typeof toRulePayload>) => Promise<unknown>,
    done: string,
    fallback: string
  ) => {
    const payload = toRulePayload(values, vocabulary);
    try {
      setSubmitting(true);
      await write(payload);
      successfulNotification("Success", done);
      form.resetFields();
      afterWrite();
      return true;
    } catch (error) {
      applyFormSubmitError(error, form, { payload, fieldNames: RULE_FORM_FIELDS, fallback, noun: "rule" });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = async (values: RuleFormValues) => {
    if (await submit(values, addForm, (p) => api.create(p), "Rule added successfully", "Failed to add rule")) {
      setAdding(false);
    }
  };

  const handleEdit = async (values: RuleFormValues) => {
    if (!editing) return;
    if (await submit(values, editForm, (p) => api.update(editing._id, p), "Rule updated successfully", "Failed to update rule")) {
      setEditing(null);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      setSubmitting(true);
      await api.remove(deleting._id);
      successfulNotification("Success", "Rule deleted successfully");
      setDeleting(null);
      afterWrite();
    } catch (error) {
      handleApiError(error, "Failed to delete rule");
    } finally {
      setSubmitting(false);
    }
  };

  const openAdd = () => {
    addForm.resetFields();
    addForm.setFieldsValue(emptyRuleFormValues());
    setAdding(true);
  };

  const openEdit = (rule: TRule) => {
    editForm.resetFields();
    editForm.setFieldsValue(ruleToFormValues(rule));
    setEditing(rule);
  };

  const activeRules = liveRules(rules);

  const intro = renderIntro?.({ activeRules, viewDeleted }) ?? (
    definitionActive && activeRules.length === 0 && !viewDeleted ? (
      <Alert
        type="warning"
        showIcon
        message="This definition has no active rules, so it never matches."
        description="Add at least one active rule. All active rules must match (AND) for the definition to win."
      />
    ) : (
      <Alert
        type="info"
        showIcon
        message={
          activeRules.length === 1
            ? "This definition wins when its one active rule matches."
            : `This definition wins when all ${activeRules.length} active rules match (AND).`
        }
        description={introDescription}
      />
    )
  );

  if (loading && rules.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 20 }}>
        <Spin size="small" /> Loading rules...
      </div>
    );
  }

  const fields = <RuleFormFields vocabulary={vocabulary} targetHelp={targetHelp} targetExtra={targetExtra} fieldPathHint={fieldPathHint} />;

  return (
    <div className={`rules-manager ${className ?? ""}`.trim()}>
      {intro && <div className="rules-manager-intro">{intro}</div>}

      <RulesTable<TRule>
        id={tableId}
        title={title}
        rules={rules}
        loading={loading}
        viewDeleted={viewDeleted}
        showAggregate={(vocabulary?.collectionTargets ?? []).length > 0 || rules.some((r) => !!r.aggregate)}
        onEdit={openEdit}
        onDelete={setDeleting}
        canEdit={canEdit}
        canDelete={canDelete}
        context={context}
        actions={
          <>
            <PermissionWrapper hasPermissions={canCreate}>
              <Button type="primary" icon={<PlusOutlined />} disabled={!canCreate} onClick={openAdd}>
                Add Rule
              </Button>
            </PermissionWrapper>
            <Space style={{ marginLeft: 16 }}>
              <span className="bar-label">View Deleted Rules</span>
              <Switch checked={viewDeleted} onChange={setViewDeleted} />
            </Space>
          </>
        }
      />

      <Modal
        title="Add Rule"
        open={adding}
        onOk={() => addForm.submit()}
        onCancel={() => setAdding(false)}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" onFinish={handleAdd}>
          {fields}
        </Form>
      </Modal>

      <Modal
        title="Edit Rule"
        open={editing !== null}
        onOk={() => editForm.submit()}
        onCancel={() => setEditing(null)}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          {fields}
        </Form>
      </Modal>

      <Modal
        title="Delete Rule"
        open={deleting !== null}
        onOk={handleDelete}
        okButtonProps={{ danger: true }}
        confirmLoading={submitting}
        onCancel={() => setDeleting(null)}
      >
        Are you sure you want to delete this rule? This is a soft delete; the rule stops counting towards the
        definition immediately.
      </Modal>
    </div>
  );
}

export default RulesManager;
